import { api, isOwner, me, send } from "../api";
import type { Role, Staff } from "../types";
import { ago, confirmButton, date, field, flash, form, h, input, link, select, show, statusChip, table, td, th, userLink } from "../ui";

const ROLES: [Role, string][] = [["viewer", "Viewer: can look"], ["admin", "Admin: can change"], ["owner", "Owner: also staff and deletions"]];

export async function staffPage() {
  if (!isOwner()) return show("Staff", "Owners only.", h("p", null, "An owner adds and removes staff and sets roles. Yours is on the ", link("/me", "Me page"), "."));
  const d = await api<{ staff: Staff[] }>("/v1/admin/staff");
  if (!d) return;
  const self = me()?.account_id;
  const by = input({ placeholder: "Email or account id", autocomplete: "off", required: "" });
  const role = select({ "aria-label": "Role" }, ROLES, "viewer");
  const addForm = form(
    "fields",
    async () => {
      const v = by.value.trim();
      await send("POST", "/v1/admin/staff", { ...(v.includes("@") ? { email: v } : { account_id: v }), role: role.value });
      await staffPage();
      flash(`${v} added as ${role.value}.`);
    },
    field("Who", by),
    field("Role", role),
    h("div", null, h("button", { class: "btn small primary", type: "submit" }, "Add")),
  );
  show(
    "Staff",
    "Who can open this site. Viewers read; admins change users, nodes, routing and blocks; owners also manage the staff, retire nodes and delete accounts.",
    table(
      [th("Who"), th("Role"), th("Added", "num"), th("Last seen", "num"), th("Bark"), th("", "num")],
      d.staff.map((s) => {
        const roleSel = select({ "aria-label": `Role of ${s.username ?? s.email ?? s.account_id}` }, ROLES.map(([r]) => [r, r]), s.role);
        roleSel.addEventListener("change", async () => {
          try {
            await send("PUT", `/v1/admin/staff/${encodeURIComponent(s.account_id)}`, { role: roleSel.value });
            flash(`${s.username ?? s.email ?? s.account_id} is now ${roleSel.value}.`);
          } catch (e) {
            roleSel.value = s.role;
            flash((e as Error).message, true);
          }
        });
        return h(
          "tr",
          null,
          h("td", null, userLink(s.account_id, s.display_name || s.username || s.email || s.account_id), h("span", { class: "muted block" }, [s.email, s.account_id].filter(Boolean).join(" · ")), s.account_id === self ? h("span", { class: "muted block" }, "you") : null),
          h("td", null, s.account_id === self ? statusChip(s.role) : roleSel),
          h("td", { class: "num", title: `by ${s.created_by}` }, date(s.created_at)),
          td(ago(s.last_seen_at), "num"),
          td(s.bark ? "set" : "—"),
          s.account_id === self
            ? td("", "num")
            : h("td", { class: "num" }, confirmButton("Remove", `Remove ${s.username ?? s.email ?? "them"}`, async () => {
                await send("DELETE", `/v1/admin/staff/${encodeURIComponent(s.account_id)}`);
                await staffPage();
                flash("Removed.");
              })),
        );
      }),
      "Nobody yet.",
    ),
    h("h2", null, "Add someone"),
    h("p", { class: "muted small" }, "They must have signed in to admin.luwu.dev once (any Luwu account can; they see the \"not on the staff\" page). Then add them by that email or account id."),
    addForm,
  );
}
